import React from 'react';
import { RefreshCw, Check, Sparkles } from 'lucide-react';

interface PortraitPickerProps {
  choices: string[];
  loading: boolean;
  canReroll: boolean;
  rerolling: boolean;
  onSelect: (index: number) => void;
  onReroll: () => void;
  onSkip?: () => void;
  selectedSlot: number | null;
  selecting: boolean;
  error?: string | null;
}

const PortraitPicker: React.FC<PortraitPickerProps> = ({
  choices,
  loading,
  canReroll,
  rerolling,
  onSelect,
  onReroll,
  onSkip,
  selectedSlot,
  selecting,
  error,
}) => {
  if (loading) {
    return (
      <div className="portrait-picker">
        <div className="portrait-picker-header">
          <h2 className="step-title">Choose Your Look</h2>
          <p className="step-sub">Forging 3 portrait options...</p>
        </div>
        <div className="portrait-picker-grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="portrait-choice portrait-choice-loading">
              <div className="portrait-spinner">
                <Sparkles size={24} className="spinner-icon" />
              </div>
              <span className="portrait-choice-label">Option {i}</span>
            </div>
          ))}
        </div>
        <div className="portrait-forging-text">
          <span className="spinner" />
          <span>AI is painting your portraits...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="portrait-picker">
      <div className="portrait-picker-header">
        <h2 className="step-title">Choose Your Look</h2>
        <p className="step-sub">Tap a portrait to select it as your player's avatar</p>
      </div>

      {error && <div className="portrait-error">{error}</div>}

      <div className="portrait-picker-grid">
        {choices.map((url, i) => {
          const isSelected = selectedSlot === i;
          return (
            <button
              key={i}
              className={`portrait-choice ${isSelected ? 'portrait-choice-selected' : ''}`}
              onClick={() => onSelect(i)}
              disabled={selecting}
            >
              <img
                src={url}
                alt={`Portrait option ${i + 1}`}
                className="portrait-choice-img"
              />
              {isSelected && (
                <div className="portrait-choice-check">
                  <Check size={20} />
                </div>
              )}
              <span className="portrait-choice-label">Option {i + 1}</span>
            </button>
          );
        })}
      </div>

      <div className="portrait-picker-actions">
        {selectedSlot ? (
          <button
            className="btn-gold btn-full"
            onClick={() => onSelect(selectedSlot)}
            disabled={selecting}
          >
            {selecting ? (
              <><span className="spinner" /> Saving...</>
            ) : (
              <><Check size={18} /> Confirm Portrait</>
            )}
          </button>
        ) : (
          <div className="portrait-pick-hint">
            Tap a portrait above to select it
          </div>
        )}

        {canReroll && (
          <button
            className="btn-ghost btn-full portrait-reroll-btn"
            onClick={onReroll}
            disabled={rerolling || selecting}
          >
            {rerolling ? (
              <><span className="spinner" /> Regenerating...</>
            ) : (
              <><RefreshCw size={14} /> Re-roll Portraits (1 remaining)</>
            )}
          </button>
        )}

        {!canReroll && !selectedSlot && (
          <span className="portrait-reroll-spent">No re-rolls remaining</span>
        )}

        {onSkip && (
          <button
            className="btn-ghost btn-full portrait-skip-btn"
            onClick={onSkip}
            disabled={selecting}
          >
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
};

export default PortraitPicker;
